import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import styles from "./ServiceSearchSelect.module.scss";

/**
 * Searchable dropdown for selecting a physio service.
 *
 * Lets the therapist type either the service name or the service code
 * to filter the list. Mirrors the lab-app `TestSearchSelect` so behavior
 * is consistent across apps.
 *
 * The options list is rendered through a React portal with `position: fixed`
 * so it escapes any parent container that uses `overflow: hidden / auto`
 * (such as scrollable tables).
 *
 * Props:
 * - services:        catalog rows. Each row should expose either
 *                    `service_code`/`test_name`/`price`/`location` (raw API)
 *                    or `serviceCode`/`testName`. The component normalizes
 *                    both shapes when rendering.
 * - value:           currently selected key. Defaults to matching `test_name`,
 *                    can be switched via `valueKey` prop.
 * - valueKey:        which catalog field is used as the selected value.
 *                    Defaults to "test_name" (physio app) but can be
 *                    overridden, e.g. "id" or "service_code".
 * - onSelect:        (selectedValue, fullServiceObject) => void
 * - loading:         boolean to render a loading state
 * - disabled:        boolean
 * - placeholder:     placeholder text
 * - emptyText:       text when no result matches the query
 */
const ServiceSearchSelect = ({
  services = [],
  value = "",
  valueKey = "test_name",
  onSelect,
  loading = false,
  disabled = false,
  placeholder = "Search by name or service code",
  emptyText = "No services match your search",
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const getName = useCallback(
    (s) => (s ? s.test_name || s.testName || s.service_name || "" : ""),
    []
  );
  const getCode = useCallback(
    (s) => (s ? s.service_code || s.serviceCode || "" : ""),
    []
  );

  const selectedService = useMemo(() => {
    if (value === "" || value === null || value === undefined) return null;
    return (
      services.find(
        (s) => String(s?.[valueKey] ?? "") === String(value)
      ) || null
    );
  }, [services, value, valueKey]);

  const selectedLabel = useMemo(() => {
    if (!selectedService) return "";
    const name = getName(selectedService);
    const code = getCode(selectedService);
    return code ? `${name} (${code})` : name;
  }, [selectedService, getName, getCode]);

  // Sync the input text with the selected service when value changes.
  useEffect(() => {
    setQuery(selectedLabel);
  }, [selectedLabel]);

  // Defensive: when parent clears the value, reset visible text and close.
  useEffect(() => {
    if (value === "" || value === null || value === undefined) {
      setQuery("");
      setOpen(false);
    }
  }, [value]);

  const updateMenuPosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, updateMenuPosition]);

  // Outside click closes the menu and restores the visible label.
  useEffect(() => {
    const handleClickOutside = (e) => {
      const inContainer =
        containerRef.current && containerRef.current.contains(e.target);
      const inList = listRef.current && listRef.current.contains(e.target);
      if (!inContainer && !inList) {
        setOpen(false);
        setQuery(selectedLabel);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selectedLabel]);

  const filteredServices = useMemo(() => {
    if (!Array.isArray(services)) return [];
    const trimmed = query.trim().toLowerCase();
    const isShowingSelected = selectedLabel && query === selectedLabel;
    if (!trimmed || isShowingSelected) return services;
    return services.filter((service) => {
      const name = getName(service).toLowerCase();
      const code = getCode(service).toLowerCase();
      return name.includes(trimmed) || code.includes(trimmed);
    });
  }, [services, query, selectedLabel, getName, getCode]);

  useEffect(() => {
    if (highlightIndex >= filteredServices.length) {
      setHighlightIndex(0);
    }
  }, [filteredServices, highlightIndex]);

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    if (!open) {
      setOpen(true);
      updateMenuPosition();
    }
    setHighlightIndex(0);
  };

  const handleFocus = () => {
    if (disabled) return;
    setOpen(true);
    updateMenuPosition();
    if (selectedService && query) {
      setQuery("");
    }
  };

  const handleSelect = (service) => {
    if (!service) return;
    const selected = service[valueKey];
    onSelect && onSelect(String(selected ?? ""), service);
    const name = getName(service);
    const code = getCode(service);
    setQuery(code ? `${name} (${code})` : name);
    setOpen(false);
    if (inputRef.current) inputRef.current.blur();
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        updateMenuPosition();
      }
      setHighlightIndex((i) =>
        Math.min(i + 1, filteredServices.length - 1)
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (open && filteredServices[highlightIndex]) {
        handleSelect(filteredServices[highlightIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (!open || !listRef.current) return;
    const item = listRef.current.querySelector(
      `[data-idx="${highlightIndex}"]`
    );
    if (item && typeof item.scrollIntoView === "function") {
      item.scrollIntoView({ block: "nearest" });
    }
  }, [highlightIndex, open]);

  const handleClear = (e) => {
    e.stopPropagation();
    onSelect && onSelect("", null);
    setQuery("");
    setOpen(true);
    updateMenuPosition();
    if (inputRef.current) inputRef.current.focus();
  };

  const showMenu = open && !disabled && !loading;

  const menu = showMenu
    ? createPortal(
        <ul
          className={styles["options-list"]}
          ref={listRef}
          role="listbox"
          style={{
            position: "fixed",
            top: menuPos.top,
            left: menuPos.left,
            width: menuPos.width,
          }}
        >
          {filteredServices.length === 0 ? (
            <li className={styles["empty"]}>{emptyText}</li>
          ) : (
            filteredServices.map((service, idx) => {
              const name = getName(service);
              const code = getCode(service);
              const optionValue = String(service?.[valueKey] ?? "");
              return (
                <li
                  key={`${optionValue || idx}-${idx}`}
                  data-idx={idx}
                  className={`${styles["option"]} ${
                    idx === highlightIndex ? styles["highlighted"] : ""
                  } ${
                    optionValue === String(value) && optionValue !== ""
                      ? styles["selected"]
                      : ""
                  }`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(service);
                  }}
                  onMouseEnter={() => setHighlightIndex(idx)}
                  role="option"
                  aria-selected={
                    optionValue === String(value) && optionValue !== ""
                  }
                >
                  <span className={styles["option-name"]}>
                    {name || "Unnamed service"}
                  </span>
                  {code && (
                    <span className={styles["option-code"]}>{code}</span>
                  )}
                </li>
              );
            })
          )}
        </ul>,
        document.body
      )
    : null;

  return (
    <div
      className={styles["search-select"]}
      ref={containerRef}
      data-disabled={disabled || undefined}
    >
      <div className={styles["input-wrapper"]}>
        <input
          ref={inputRef}
          type="text"
          className={styles["search-input"]}
          value={query}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={loading ? "Loading services..." : placeholder}
          disabled={disabled || loading}
          autoComplete="off"
        />
        {value && !disabled && !loading && (
          <button
            type="button"
            className={styles["clear-btn"]}
            onClick={handleClear}
            aria-label="Clear selection"
          >
            ×
          </button>
        )}
        <span className={styles["chevron"]} aria-hidden="true">
          ▾
        </span>
      </div>

      {menu}
    </div>
  );
};

export default ServiceSearchSelect;
